import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { teamService } from '../../services/teamService';
import EntityFinancialRecordsPanel from '../../components/finance/EntityFinancialRecordsPanel';

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon name={icon} size={16} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-foreground font-medium break-words">{value || 'N/A'}</p>
    </div>
  </div>
);

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'pending_approval' || status === 'pending') return 'bg-warning/10 text-warning';
  return 'bg-muted text-muted-foreground';
};

const ClubDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadClub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadClub = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await teamService.getById(id);
      setClub(data);
      document.title = `${data?.name || 'Club'} - Tanzania Basketball Federation`;
    } catch (err) {
      setError(err.message || 'Failed to load club');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => navigate('/clubs')} className="mb-6">
        Back to Clubs
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading club...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !club ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Shield" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Club not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6 flex items-center gap-6 flex-wrap">
            <div className="w-20 h-20 rounded-lg bg-green-700/10 flex items-center justify-center overflow-hidden shrink-0">
              {club?.logoUrl ? (
                <Image src={club.logoUrl} alt={club.name} className="w-full h-full object-cover" />
              ) : (
                <Icon name="Shield" size={36} className="text-green-700" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{club?.name || 'Unnamed Club'}</h1>
              <p className="text-muted-foreground">
                {[club?.city, club?.region].filter(Boolean).join(', ') || 'Location N/A'}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusBadgeClass(club?.teamStatus)}`}>
                  {(club?.teamStatus || 'pending').replace(/_/g, ' ')}
                </span>
                {club?.category && (
                  <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary capitalize">
                    {club.category}
                  </span>
                )}
                {club?.division && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent/10 text-accent-foreground capitalize">
                    {club.division}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Club Information</h2>
              <div className="space-y-4">
                <InfoRow icon="CalendarDays" label="Founded" value={club?.foundedYear} />
                <InfoRow icon="MapPin" label="Home Venue" value={club?.homeVenue} />
                <InfoRow icon="Home" label="Address" value={club?.address} />
                <InfoRow icon="Mail" label="Contact Email" value={club?.contactEmail} />
                <InfoRow icon="Phone" label="Contact Phone" value={club?.primaryPhone} />
                {club?.website && (
                  <InfoRow icon="Globe" label="Website" value={club.website} />
                )}
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Organizational Structure</h2>
              <div className="space-y-4">
                <InfoRow icon="User" label="President" value={club?.presidentName} />
                <InfoRow icon="User" label="Secretary" value={club?.secretaryName} />
                <InfoRow icon="UserCheck" label="Head Coach" value={club?.coachName} />
                {club?.teamManager?.fullName && (
                  <InfoRow icon="UserCog" label="Team Manager" value={club.teamManager.fullName} />
                )}
              </div>
            </div>
          </div>

          <EntityFinancialRecordsPanel
            entityType="club"
            entityId={club?.id}
            entityName={club?.name}
            region={club?.region}
          />
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default ClubDetailPage;
